import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const CACHE_KEY = 'maintenance_status';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedStatus {
  isEnabled: boolean;
  timestamp: number;
  settings?: any;
}

export function useMaintenanceCheck() {
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed: CachedStatus = JSON.parse(cached);
          const isExpired = Date.now() - parsed.timestamp > CACHE_DURATION;
          
          if (!isExpired) {
            setIsEnabled(parsed.isEnabled);
            setSettings(parsed.settings);
            setIsLoading(false);
            return;
          }
        }

        // Fetch from database if cache miss or expired
        const { data, error } = await supabase
          .from('maintenance_settings')
          .select('*')
          .single();

        if (error) throw error;

        const status: CachedStatus = {
          isEnabled: data.is_enabled,
          timestamp: Date.now(),
          settings: data
        };

        // Cache the result
        localStorage.setItem(CACHE_KEY, JSON.stringify(status));
        
        setIsEnabled(data.is_enabled);
        setSettings(data);
      } catch (err) {
        console.error('Error checking maintenance:', err);
        setIsEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenance();
  }, []);

  return { isEnabled, settings, isLoading };
}

// Clear cache when maintenance status changes (call from admin panel)
export const clearMaintenanceCache = () => {
  localStorage.removeItem(CACHE_KEY);
};
